import * as React from 'react'
import { useSelector } from 'react-redux'
import { Box, Typography as MIUTypography, styled } from '@mui/material'
import { DataGrid, gridClasses, esES } from '@mui/x-data-grid'
import { getPublicEventFeedback, getPublicEventsAll } from '../../../services'
import { grey } from '@mui/material/colors'
import { getColumns } from './columns'
import Botton from '../../BottonCustom'
import { Create } from '@mui/icons-material'
import CreationModal from './CreationModal'
import EditModal from './EditModal'
import ViewModal from './ViewModal'
import DeletePublicEventModal from './DeletePublicEventModal'
import AlertCustom from '../../Alerts'
import { usePersistedState } from '../../../hooks/usePersistedState'

const styleContainer = { height: '100%', width: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }

const styleDefault = theme => ({
  color: theme.palette.primary.main,
  border: `1px solid ${theme.palette.primary.main}`,
  width: '100%',
  fontSize: '12px',
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
  '&:active': { boxShadow: 'none', opacity: 0.9 },
})

const styleHeaderTab = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignContent: 'center',
  alignItems: 'baseline',
  ml: 4,
  mb: 1,
}

const Typography = styled(MIUTypography)(props => ({
  fontFamily: props.theme.typography.fontFamily,
  fontStyle: 'normal',
  fontSize: '1em',
  fontWeight: 650,
  lineHeight: '18px',
  color: '#6A6A6A',
  paddingBottom: 6,
  paddingTop: 6,
  textAlign: 'center',
  marginTop: 12,
  marginBottom: 3,
  height: '5%',
}))

const PublicEventsTable = () => {
  const modalRef = React.useRef()
  const editModalRef = React.useRef()
  const viewModalRef = React.useRef()
  const deleteModalRef = React.useRef()
  const alertRef = React.useRef()

  const [data, setData] = React.useState([])
  const [pageSize, setPageSize] = usePersistedState('admin.publicEvents.pageSize', 25)
  const [rowId, setRowId] = React.useState(null)
  const [expandedRowIds, setExpandedRowIds] = React.useState([])
  const [feedbackByEventId, setFeedbackByEventId] = React.useState({})
  const feedbackByEventIdRef = React.useRef({})

  const { isStored } = useSelector(state => state.alertsGlobal)

  const handleEdit = React.useCallback(row => {
    editModalRef.current.openModal(row)
  }, [])

  const handleView = React.useCallback(row => {
    viewModalRef.current.openModal(row)
  }, [])

  const handleRequestDelete = React.useCallback(row => {
    deleteModalRef.current?.openModal(row)
  }, [])

  React.useEffect(() => {
    feedbackByEventIdRef.current = feedbackByEventId
  }, [feedbackByEventId])

  const loadFeedbackByEventId = React.useCallback(async (eventId, force = false) => {
    const current = feedbackByEventIdRef.current[eventId]
    if (!force && (current?.status === 'loading' || current?.status === 'success')) return

    setFeedbackByEventId(prev => ({
      ...prev,
      [eventId]: {
        status: 'loading',
        feedback: prev[eventId]?.feedback || [],
        averageRating: prev[eventId]?.averageRating ?? null,
        error: null,
      },
    }))

    try {
      const response = await getPublicEventFeedback(eventId)
      setFeedbackByEventId(prev => ({
        ...prev,
        [eventId]: {
          status: 'success',
          feedback: Array.isArray(response?.feedback) ? response.feedback : [],
          averageRating: response?.averageRating ?? null,
          error: null,
        },
      }))
    } catch (error) {
      setFeedbackByEventId(prev => ({
        ...prev,
        [eventId]: {
          status: 'error',
          feedback: prev[eventId]?.feedback || [],
          averageRating: prev[eventId]?.averageRating ?? null,
          error: error?.message || 'No fue posible cargar comentarios del evento.',
        },
      }))
    }
  }, [])

  const handleRetryFeedback = React.useCallback(eventId => {
    loadFeedbackByEventId(eventId, true)
  }, [loadFeedbackByEventId])

  const onToggleRow = React.useCallback(rowIdToToggle => {
    setExpandedRowIds(prev =>
      prev.includes(rowIdToToggle)
        ? prev.filter(id => id !== rowIdToToggle)
        : [...prev, rowIdToToggle],
    )
  }, [])

  const columns = React.useMemo(
    () =>
      getColumns(
        rowId,
        setRowId,
        handleEdit,
        expandedRowIds,
        onToggleRow,
        feedbackByEventId,
        handleRetryFeedback,
        handleView,
        handleRequestDelete,
      ),
    [
      rowId,
      handleEdit,
      expandedRowIds,
      onToggleRow,
      feedbackByEventId,
      handleRetryFeedback,
      handleView,
      handleRequestDelete,
    ],
  )

  const rows = React.useMemo(() => {
    const expanded = new Set(expandedRowIds)
    const mappedRows = []

    data.forEach(event => {
      mappedRows.push(event)
      if (expanded.has(event.id)) {
        mappedRows.push({
          ...event,
          id: `detail-${event.id}`,
          __isDetail: true,
          __parentId: event.id,
        })
      }
    })

    return mappedRows
  }, [data, expandedRowIds])

  const handleFormCreation = () => {
    modalRef.current.openModal()
  }

  const loadData = () => {
    getPublicEventsAll()
      .then(events => setData(events))
      .catch(err => console.log('Error cargando eventos:', err))
  }

  React.useEffect(() => {
    loadData()
  }, [])

  React.useEffect(() => {
    if (!Boolean(isStored)) return
    loadData()
    alertRef?.current?.openAlert(true)
  }, [isStored])

  React.useEffect(() => {
    setExpandedRowIds(prev =>
      prev.filter(expandedId => data.some(event => event.id === expandedId)),
    )
  }, [data])

  React.useEffect(() => {
    const validIds = new Set(data.map(event => String(event.id)))
    setFeedbackByEventId(prev => {
      const next = {}
      Object.entries(prev).forEach(([eventId, detail]) => {
        if (validIds.has(String(eventId))) next[eventId] = detail
      })
      return next
    })
  }, [data])

  React.useEffect(() => {
    expandedRowIds.forEach(eventId => {
      const feedbackState = feedbackByEventId[eventId]
      if (!feedbackState) loadFeedbackByEventId(eventId)
    })
  }, [expandedRowIds, feedbackByEventId, loadFeedbackByEventId])

  return (
    <Box sx={styleContainer}>
      <Box sx={styleHeaderTab}>
        <Box>
          <Typography>Administrar eventos de Prueba</Typography>
        </Box>
        <Box>
          <Botton
            text="Nuevo"
            onClick={handleFormCreation}
            startIcon={<Create />}
            styleButton={styleDefault}
            size="small"
          />
        </Box>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', px: 2, display: 'flex' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={pageSize}
          getRowId={row => row.id}
          rowsPerPageOptions={[25, 50, 75]}
          onPageSizeChange={newPageSize => setPageSize(newPageSize)}
          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
          isCellEditable={params =>
            !params.row?.__isDetail && Boolean(params.colDef.editable)
          }
          getRowHeight={params => params.model?.__isDetail ? 'auto' : null}
          getEstimatedRowHeight={() => 180}
          getRowClassName={params => params.row?.__isDetail ? 'event-detail-row' : ''}
          getRowSpacing={params => ({
            top: params.model?.__isDetail ? 0 : params.isFirstVisible ? 0 : 5,
            bottom: params.model?.__isDetail ? 8 : 0,
          })}
          sx={{
            flex: 1,
            [`& .${gridClasses.row}`]: {
              bgcolor: grey[100],
            },
            '& .event-detail-row': {
              bgcolor: '#F9FAFC !important',
            },
            '& .event-detail-cell': {
              py: '0 !important',
              alignItems: 'flex-start',
            },
            '& .event-expand-cell': {
              pl: 0.5,
            },
          }}
          onCellEditCommit={params => {
            if (typeof params.id === 'string' && params.id.startsWith('detail-')) {
              return
            }
            setRowId(params.id)
          }}
        />
      </Box>
      <CreationModal ref={modalRef} />
      <EditModal ref={editModalRef} />
      <ViewModal ref={viewModalRef} />
      <DeletePublicEventModal ref={deleteModalRef} />
      <AlertCustom
        ref={alertRef}
        severity={isStored?.stored !== true ? 'warning' : 'success'}>
        {isStored?.stored !== true ? isStored?.message : 'Guardado exitosamente.'}
      </AlertCustom>
    </Box>
  )
}

export default PublicEventsTable
